import React from 'react';
import { AlertTriangle, RefreshCcw, ArrowLeft } from 'lucide-react';

/**
 * ErrorBoundary - Catches React rendering errors and shows a fallback UI
 * instead of a blank white page. Wraps components that render dynamic data
 * which could potentially contain non-string values passed as React children.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught rendering error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-brand-dark flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-brand-black-light border border-red-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-3">
              Erreur d'affichage
            </h2>
            <p className="text-gray-400 mb-6 text-sm">
              Une erreur est survenue lors de l'affichage de cette page.
              Cela n'affecte pas vos données.
            </p>
            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                  Détails techniques
                </summary>
                <pre className="mt-2 p-3 bg-brand-dark rounded-lg text-xs text-red-400/70 overflow-auto max-h-32 font-mono">
                  {String(this.state.error)}
                </pre>
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleGoBack}
                className="px-4 py-2.5 bg-brand-dark border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <button
                onClick={this.handleRetry}
                className="px-4 py-2.5 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <RefreshCcw className="w-4 h-4" />
                Réessayer
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
